import moment from "moment";

import { Component, ViewChild } from "@angular/core";
import { Platform, Nav, ModalController, MenuController } from "ionic-angular";
import { StatusBar } from "@ionic-native/status-bar";
import { SplashScreen } from "@ionic-native/splash-screen";

import { Actions } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { FCM } from "@ionic-native/fcm";
import { Badge } from "@ionic-native/badge";

import { AppState } from "./app.reducer";
import { RealtimeService } from "../services/realtime.service";
import { AuthService } from "../services/auth.service";
import { UserService } from "../services/user.service";
import { PlatformService } from "../services/platform.service";
import { SystemService } from "../services/system.service";
import { AlarmService } from "../services/alarm.service";
import { CategoryService } from "../services/category.service";

import { AuthState } from "../store/auth/auth.state";

import * as AuthActions from "../store/auth/auth.action";
import { User } from "../models/user.model";
import { TranslateService } from "@ngx-translate/core";
import { TransService } from "../services/trans.service";
import { ManagerService } from "../services/manager.service";
import { ImageService } from "../services/image.service";
import { getUserProfileUrl } from "../common/utils";
import { FacebookService } from "../services-sns/facebook.service";
import { LineService } from "../services-sns/line.service";
import { ChannelCategoryService } from "../services/channel-category.service";

@Component({
  templateUrl: "app.html"
})
export class MyApp {
  @ViewChild(Nav)
  public nav: Nav;

  rootPage: string;

  isIOSXApp = false;

  constructor(
    private modalCtrl: ModalController,
    public imageService: ImageService,
    public managerService: ManagerService,
    private transService: TransService,
    private categoryService: CategoryService,
    private channelCategoryService: ChannelCategoryService,
    private store: Store<AppState>,
    private realtimeService: RealtimeService,
    public authService: AuthService,
    private fcm: FCM,
    private userService: UserService,
    private platformService: PlatformService,
    private alarmService: AlarmService,
    private systemService: SystemService,
    private menuController: MenuController,
    private badge: Badge,
    private actions$: Actions,
    private platform: Platform,
    private translate: TranslateService,
    private facebookService: FacebookService,
    private lineService: LineService,
    statusBar: StatusBar,
    splashScreen: SplashScreen
  ) {
    moment.locale("en");
    translate.setDefaultLang("en");
    translate.use("en");

    platform
      .ready()
      .then(() => {
        this.menuController.swipeEnable(false);

        if (this.platform.is("cordova")) {
          statusBar.styleDefault();
        }

        this.setupFcm();
        this.updateToken();

        return new Promise(resolve => {
          this.platformService
            .init()
            .then(() => {
              this.setLanguage(this.platformService.getLanguage());
              setTimeout(() => {
                resolve();
              }, 500);
            })
            .catch(e => {
              setTimeout(() => {
                resolve();
              }, 500);
            });
        });
      })
      .then(() => {
        if (this.platform.is("cordova")) {
          splashScreen.hide();
        }
      })
      .then(() => {
        this.facebookService.init();
        this.lineService.init();
        this.realtimeService.setup();
        this.isIOSXApp = this.platformService.isIOSXApp();
        this.categoryService.fetchCategories();
        this.channelCategoryService.fetchCategories();

        this.authService
          .setup()
          .then(() => this.store.dispatch(new AuthActions.TryLogin()));

        this.store.select("auth").subscribe((auth: AuthState) => {
          if (auth && auth.loading) return;

          if (auth && auth.login) {
            this.realtimeService.subscribe(auth.user);

            this.updateToken();
            this.badge.set(auth.user.badge);

            if (auth.user.role === "ACCOUNT")
              this.setLanguage(auth.user.language);
            else this.setLanguage("KOREAN");

            this.categoryService.updatedCategories(null);
          } else {
            this.realtimeService.unsubscribe();
            this.badge.set(0);
          }
        });
      });

    this.systemService.fetchSystems();

    this.actions$.ofType(AuthActions.TRYLOGIN_SUCCESS).subscribe(() => {
      this.platformService.setRoot(this.nav, "TabbarPage");
    });

    this.actions$.ofType(AuthActions.TRYLOGIN_ERROR).subscribe(() => {
      this.platformService.setFirstPage(this.nav);
    });
  }

  private setLanguage(language) {
    switch (language) {
      case "CHINESE":
        this.translate.use("cn");
        moment.locale("zh_CN");
        this.transService.reload("cn");
        break;
      case "JAPANESE":
        this.translate.use("ja");
        moment.locale("ja");
        this.transService.reload("ja");
        break;
      case "KOREAN":
        this.translate.use("ko");
        moment.locale("ko");
        this.transService.reload("ko");
        break;
      default:
        this.translate.use("en");
        moment.locale("en");
        this.transService.reload("en");
        break;
    }
  }

  private updateToken() {
    if (this.platformService.isWeb()) return;

    this.fcm.getToken().then(token => this.updatePushToken(token));
  }

  private updatePushToken(token) {
    if (
      token &&
      this.authService.user &&
      this.authService.user.pushToken !== token
    ) {
      this.authService.user.pushToken = token; // prevent to called many times.
      this.userService
        .updateUser(this.authService.user._id, {
          pushToken: token
        } as User)
        .subscribe();
    }
  }

  private setupFcm() {
    if (this.platformService.isWeb()) return;

    this.platform.resume.subscribe(() => {
      this.fcm.getToken().then(token => this.updatePushToken(token));
    });

    this.fcm.onTokenRefresh().subscribe(token => this.updatePushToken(token));

    this.alarmService.setupOn();
    this.fcm.onNotification().subscribe(data => {
      try {
        data.doc = JSON.parse(data.doc);
      } catch (e) {}

      if (data.badge) this.badge.set(+data.badge);
      // if (data.wasTapped) {
      this.alarmService.setPage(data);
      // }
    });
  }

  goCelebEdit() {
    this.nav.push("CelebEditPage", {
      id: this.managerService.celebSelected._id
    });
  }

  goSetting() {
    this.nav.push("SettingMainPage");
  }

  onSwitchAccount() {
    const modal = this.modalCtrl.create("SelectSwitchPage", {
      celebs: this.managerService.members || [this.managerService.celebSelected]
    });

    modal.onDidDismiss(data => {
      if (data) {
        this.managerService.switchAccount(data);
      }
    });
    modal.present();
  }

  getProfileUrl(user) {
    if (this.authService.isGuest) {
      return `assets/imgs/profile-default.svg`;
    }

    if (!user) return "";

    return getUserProfileUrl(
      user.patternId,
      user.photoId,
      user.photoUrl,
      this.imageService
    );
  }

  goFavorite() {
    this.nav.push("FavoritePage");
  }

  goMyRequests() {
    this.nav.push("MyRequestsPage");
  }

  goMyMessages() {
    this.nav.push("MyMessagesPage");
  }

  goAdmin() {
    this.nav.push("AdminPage");
  }

  goMyProfile() {
    if (this.authService.isGuest) {
      this.nav.push("IntroPage");
      return;
    }
    this.nav.push("SettingProfilePage");
  }
}



// WEBPACK FOOTER //
// ./src/app/app.component.ts